/**
 * Work Items Dashboard page.
 *
 * Wires the pure `workItemsReducer` to a side-effect layer that drives the
 * counts and items APIs. All subcomponents (RefreshButton, CountsDashboard,
 * PendingTasksCard, ProcessPathCard, WorkItemsTable, PaginationControls)
 * are defined inline because each stays small enough that co-location is
 * easier to review than fragmenting the file layout.
 */

import React, {
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from "react";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import DataTable, {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
} from "@amzn/meridian/data-table";
import Heading from "@amzn/meridian/heading";
import Loader from "@amzn/meridian/loader";
import Row from "@amzn/meridian/row";
import Tag from "@amzn/meridian/tag";
import Text from "@amzn/meridian/text";

import MetricCard from "../../components/MetricCard";
import { useAppDispatch, useAppSelector } from "../../store";
import {
    selectStationCode,
    setStationCode,
} from "../../store/slices/shiftPlanSlice";
import { showError } from "../../store/slices/errorSlice";
import {
    WORK_ITEMS_PAGE_SIZE,
    WORK_ITEMS_START_DATE_DAYS,
    WorkItemStatus,
} from "../../utils/constants";
import StartContinueTaskButton from "./StartContinueTaskButton";

import {
    fetchWorkItemCounts,
    fetchWorkItems,
    resolveWarehouseId,
} from "./api";
import {
    ActiveFilter,
    GetWorkItemsRequest,
    WorkItemRow,
    WorkItemsState,
    WorkItemSummary,
} from "./model";
import {
    initialWorkItemsState,
    workItemsReducer,
} from "./reducer";
import {
    selectProcessPathCards,
    selectRenderedRows,
} from "./selectors";

import "./index.scss";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Columns rendered by the work items table in fixed order. This array is
 * the single source of truth for the header row.
 *
 * AGE is computed as `now − lastWorkItemStatusUpdatedTime` and rendered via
 * `formatDuration`.
 */
export const TABLE_COLUMNS: readonly string[] = [
    "PRIORITY",
    "TASK NAME",
    "PROCESS",
    "STARTED BY",
    "AGE",
    "ACTION",
] as const;

/**
 * Columns that support page-local client-side sort, mapped to their
 * TABLE_COLUMNS label. Clicking the header cycles none → asc → desc → none.
 */
const SORTABLE_COLUMNS: ReadonlyArray<{
    label: string;
    key: "priority" | "age";
}> = [
    { label: "PRIORITY", key: "priority" },
    { label: "AGE", key: "age" },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Inline subcomponents
// ---------------------------------------------------------------------------

/**
 * Refresh control. Dispatches REFRESH; the reducer preserves the selected
 * process path and clears buffer / cache / counts.
 */
const RefreshButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <Button type="tertiary" size="small" onClick={onClick}>
        Refresh
    </Button>
);

/**
 * Clickable summary card for "Pending Tasks". When clicked, the page clears
 * any active process-path filter. The card is visually marked "selected"
 * when no filter is active, signalling that all pending tasks are shown.
 */
const PendingTasksCard: React.FC<{
    count: number | null;
    selected: boolean;
    onClick: () => void;
}> = ({ count, selected, onClick }) => (
    <Box
        type="outline"
        spacingInset="300"
        backgroundColor={selected ? "highlight" : undefined}
        data-testid="pending-tasks-card"
        data-selected={selected}
    >
        <button
            type="button"
            onClick={onClick}
            aria-label="Pending Tasks"
            aria-pressed={selected}
            style={{
                all: "unset",
                cursor: "pointer",
                display: "block",
                width: "100%",
            }}
        >
            <Column spacing="200">
                <Text type="h100" color="secondary">
                    Pending Tasks
                </Text>
                {count === null ? (
                    <Loader size="small" />
                ) : (
                    <Heading level={1}>{count}</Heading>
                )}
            </Column>
        </button>
    </Box>
);

/**
 * Clickable card for a single process path. Exactly one card is marked
 * selected when `state.selectedProcessPath` matches its name.
 */
const ProcessPathCard: React.FC<{
    name: string;
    count: number;
    selected: boolean;
    onClick: () => void;
}> = ({ name, count, selected, onClick }) => (
    <Box
        type="outline"
        spacingInset="300"
        backgroundColor={selected ? "highlight" : undefined}
        data-testid={`process-path-card-${name}`}
        data-selected={selected}
    >
        <button
            type="button"
            onClick={onClick}
            aria-label={name}
            aria-pressed={selected}
            style={{
                all: "unset",
                cursor: "pointer",
                display: "block",
                width: "100%",
            }}
        >
            <Column spacing="200">
                <Text type="h100" color="secondary">
                    {name}
                </Text>
                <Heading level={1}>{count}</Heading>
            </Column>
        </button>
    </Box>
);

/**
 * Counts dashboard — lays out the Pending Tasks card, the three fixed
 * metric cards (High Priority / Completed Today / Expired Today), and the
 * dynamic list of process-path cards. While counts are loading, fixed
 * values render a loader in place of the number.
 */
const CountsDashboard: React.FC<{
    state: WorkItemsState;
    onClearFilter: () => void;
    onSelectProcessPath: (name: string) => void;
}> = ({ state, onClearFilter, onSelectProcessPath }) => {
    const counts = state.counts;
    const processPathCards = selectProcessPathCards(state);
    const isCountsLoading = state.status === "loading_counts";

    // Shared rendering helper: loader while counts are null OR a request
    // is in flight; otherwise the numeric value.
    const renderCountValue = (value: number | undefined): string => {
        if (counts === null || isCountsLoading) {
            return "—";
        }
        return (value ?? 0).toString();
    };

    return (
        <Column spacing="300" spacingInset="300 none">
            <Row spacing="300" wrap="down" alignmentVertical="stretch">
                <PendingTasksCard
                    count={
                        counts === null || isCountsLoading
                            ? null
                            : counts.pendingTasksCount
                    }
                    selected={state.selectedProcessPath === null}
                    onClick={onClearFilter}
                />
                <MetricCard
                    title="High Priority"
                    value={renderCountValue(counts?.highPriorityCount)}
                />
                <MetricCard
                    title="Completed Today"
                    value={renderCountValue(counts?.completedTodayCount)}
                />
                <MetricCard
                    title="Expired Today"
                    value={renderCountValue(counts?.expiredTodayCount)}
                />
            </Row>
            {processPathCards.length > 0 && (
                <Row spacing="300" wrap="down" alignmentVertical="stretch">
                    {processPathCards.map((card) => (
                        <ProcessPathCard
                            key={card.name}
                            name={card.name}
                            count={card.count}
                            selected={card.selected}
                            onClick={() =>
                                onSelectProcessPath(card.name)
                            }
                        />
                    ))}
                </Row>
            )}
        </Column>
    );
};

/**
 * Work items table. Renders the six fixed columns from TABLE_COLUMNS in
 * the required order. The body branches on status:
 *   - `loading_items` with no cache entry → table-wide loader.
 *   - `error` → inline error row.
 *   - zero rows with no buffer and no nextToken → empty-state row.
 *   - otherwise → one row per WorkItemRow from selectRenderedRows.
 */
const WorkItemsTable: React.FC<{
    state: WorkItemsState;
    rows: WorkItemRow[];
    onToggleSort: (column: "priority" | "age") => void;
}> = ({ state, rows, onToggleSort }) => {
    const pageCacheEntry = state.pageCache[state.currentPage];
    const isLoadingCurrentPage =
        state.status === "loading_items" && pageCacheEntry === undefined;
    const isError = state.status === "error";

    // Lookup so the per-row `StartContinueTaskButton` can receive the raw
    // `WorkItemSummary` for the displayed row even though the view-model
    // `WorkItemRow` only carries presentation fields. `pageCacheEntry` may
    // be undefined while a page is still loading — callers short-circuit
    // to the loading branch above, so the empty-map default is only
    // reached on a transient race.
    const summariesById: Record<string, WorkItemSummary> = React.useMemo(
        () =>
            (pageCacheEntry ?? []).reduce<Record<string, WorkItemSummary>>(
                (acc, s) => {
                    acc[s.workItemId] = s;
                    return acc;
                },
                {},
            ),
        [pageCacheEntry],
    );

    // Empty terminal state: ready table, no buffer, no nextToken,
    // and the current page yielded zero rows.
    const isEmpty =
        state.status === "ready" &&
        rows.length === 0 &&
        state.forwardBuffer.length === 0 &&
        state.nextToken === null;

    /**
     * Render a header cell. For sortable columns (PRIORITY, AGE) the
     * header becomes a clickable control that cycles the reducer's sort
     * state through none → asc → desc → none. A small arrow suffix
     * indicates the current direction so the interaction is discoverable.
     */
    const renderHeaderCell = (col: string) => {
        const sortable = SORTABLE_COLUMNS.find((c) => c.label === col);
        if (!sortable) {
            return <DataTableHeader key={col}>{col}</DataTableHeader>;
        }

        const isActive = state.sort.column === sortable.key;
        const indicator =
            isActive && state.sort.direction === "asc"
                ? " ▲"
                : isActive && state.sort.direction === "desc"
                  ? " ▼"
                  : "";

        return (
            <DataTableHeader key={col}>
                <Button
                    type="link"
                    size="small"
                    onClick={() => onToggleSort(sortable.key)}
                    aria-label={`Sort by ${col}`}
                >
                    {col}
                    {indicator}
                </Button>
            </DataTableHeader>
        );
    };

    const renderBody = () => {
        if (isLoadingCurrentPage) {
            return (
                <DataTableRow>
                    <DataTableCell colSpan={TABLE_COLUMNS.length}>
                        <Row alignmentHorizontal="center" spacingInset="400">
                            <Loader size="medium" label="Loading work items" />
                        </Row>
                    </DataTableCell>
                </DataTableRow>
            );
        }

        if (isError) {
            return (
                <DataTableRow>
                    <DataTableCell colSpan={TABLE_COLUMNS.length}>
                        <Text color="error">
                            {state.errorMessage ??
                                "Unable to load work items."}
                        </Text>
                    </DataTableCell>
                </DataTableRow>
            );
        }

        if (isEmpty) {
            return (
                <DataTableRow>
                    <DataTableCell colSpan={TABLE_COLUMNS.length}>
                        <Text color="secondary">
                            No active work items for the current filter.
                        </Text>
                    </DataTableCell>
                </DataTableRow>
            );
        }

        return rows.map((row) => {
            const summary = summariesById[row.id];
            return (
                <DataTableRow key={row.id}>
                    <DataTableCell>
                        {row.priorityTagType ? (
                            <Tag type={row.priorityTagType}>
                                {row.priorityLabel}
                            </Tag>
                        ) : (
                            <Text>{row.priorityLabel}</Text>
                        )}
                    </DataTableCell>
                    <DataTableCell>{row.taskName}</DataTableCell>
                    <DataTableCell>{row.process}</DataTableCell>
                    <DataTableCell>{row.startedBy}</DataTableCell>
                    <DataTableCell>{row.age}</DataTableCell>
                    <DataTableCell>
                        {summary ? (
                            <StartContinueTaskButton row={summary} />
                        ) : null}
                    </DataTableCell>
                </DataTableRow>
            );
        });
    };

    return (
        <DataTable
            size="medium"
            showRowDividers
            showColumnDividers={false}
            showStripes={false}
            showBorder
        >
            <DataTableHead>
                <DataTableRow>
                    {TABLE_COLUMNS.map((col) => renderHeaderCell(col))}
                </DataTableRow>
            </DataTableHead>
            <DataTableBody>{renderBody()}</DataTableBody>
        </DataTable>
    );
};

/**
 * Pagination controls. Next-button enablement is derived from reducer
 * state:
 *
 *   canGoNext = pageCache[currentPage + 1] !== undefined
 *            || forwardBuffer.length >= PAGE_SIZE
 *            || nextToken !== null
 *
 * `canGoPrev` is simply `currentPage > 1`.
 */
const PaginationControls: React.FC<{
    currentPage: number;
    canGoPrev: boolean;
    canGoNext: boolean;
    onPrev: () => void;
    onNext: () => void;
}> = ({ currentPage, canGoPrev, canGoNext, onPrev, onNext }) => (
    <Row alignmentHorizontal="end" spacing="300" spacingInset="300 none">
        <Button
            type="secondary"
            size="small"
            disabled={!canGoPrev}
            onClick={onPrev}
        >
            Previous
        </Button>
        <Text>{`Page ${currentPage}`}</Text>
        <Button
            type="secondary"
            size="small"
            disabled={!canGoNext}
            onClick={onNext}
        >
            Next
        </Button>
    </Row>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute the ISO-8601 UTC `startDate` — `now - WORK_ITEMS_START_DATE_DAYS`
 * days. Pure and time-injected so tests can feed a fixed clock.
 */
const computeStartDate = (now: Date): string =>
    new Date(now.getTime() - WORK_ITEMS_START_DATE_DAYS * MS_PER_DAY).toISOString();

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export interface WorkItemsProps {
    /**
     * Injectable clock for deterministic tests. Production callers omit
     * this and take the default `() => new Date()`.
     */
    now?: () => Date;
}

const WorkItems: React.FC<WorkItemsProps> = ({ now = () => new Date() }) => {
    const dispatch = useAppDispatch();
    const reduxStationCode = useAppSelector(selectStationCode);

    // ---- Warehouse resolution ---------------------------------------------
    //
    // `resolveWarehouseId` is pure; we feed it the current Redux value and
    // the URL query parameter, then dispatch any returned effects through
    // Redux. The resolution is recomputed whenever Redux's `stationCode`
    // changes (e.g. after this page dispatches `setStationCode` from the
    // URL-fallback branch).
    const urlStationCode = useMemo(() => {
        // `window.location.search` is only read at render time; it is not
        // reactive but it does not need to be — station-code propagation
        // happens via Redux once the URL value is seeded.
        return new URLSearchParams(window.location.search).get("stationCode");
    }, []);

    const resolution = useMemo(
        () =>
            resolveWarehouseId({
                reduxStationCode,
                urlStationCode,
            }),
        [reduxStationCode, urlStationCode],
    );

    // Apply resolver effects (setStationCode / showError) exactly once per
    // change in `resolution.effects`. The resolver returns the same effect
    // list given the same inputs, so a reference-equality check via
    // `resolution` is stable under React 18's strict-mode double-render.
    const lastDispatchedEffectsRef = useRef<typeof resolution.effects | null>(
        null,
    );
    useEffect(() => {
        if (lastDispatchedEffectsRef.current === resolution.effects) {
            return;
        }
        lastDispatchedEffectsRef.current = resolution.effects;

        for (const effect of resolution.effects) {
            if (effect.type === "setStationCode") {
                dispatch(
                    setStationCode({ stationCode: effect.stationCode }),
                );
            } else if (effect.type === "showError") {
                dispatch(
                    showError({
                        title: effect.title,
                        message: effect.message,
                    }),
                );
            }
        }
    }, [dispatch, resolution]);

    // ---- Reducer initialization -------------------------------------------
    //
    // `startDate` is computed once at mount and held stable across
    // refreshes within the same session. If the warehouse id is not
    // resolvable, we still build a reducer with an empty warehouseId and
    // stay in `idle` — no fetch is issued.
    const initialFilter: ActiveFilter = useMemo(
        () => ({
            warehouseId: resolution.warehouseId ?? "",
            processPaths: [],
            status: WorkItemStatus.ACTIVE,
            startDate: computeStartDate(now()),
        }),
        // `now` is intentionally stable in production; tests that need a
        // fresh `startDate` across remounts will pass a new `now` prop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const [state, dispatchAction] = useReducer(
        workItemsReducer,
        initialFilter,
        initialWorkItemsState,
    );

    // Keep a ref of the latest `activeFilter` so async effect callbacks
    // can compare the filter captured at request time against the current
    // filter without stale-closure bugs.
    const activeFilterRef = useRef<ActiveFilter>(state.activeFilter);
    useEffect(() => {
        activeFilterRef.current = state.activeFilter;
    }, [state.activeFilter]);

    // ---- Mount + warehouse-change: drive the (re)fetch lifecycle ---------
    //
    // First time we resolve a warehouseId, kick off the initial COUNTS_REQUEST.
    // On subsequent changes (operator picks a different station from the
    // header dropdown), dispatch SET_WAREHOUSE_ID so the reducer rebinds
    // activeFilter, clears counts/buffer/cache/token, and flips to
    // loading_counts — the effect layer then re-issues both fetches under
    // the new warehouse.
    const lastWarehouseIdRef = useRef<string | null>(null);
    useEffect(() => {
        const resolved = resolution.warehouseId;
        if (!resolved) return;
        if (resolved === lastWarehouseIdRef.current) return;

        if (lastWarehouseIdRef.current === null) {
            // First resolved warehouseId — initialFilter already captured
            // it, so we just need to kick off the counts request.
            dispatchAction({ type: "COUNTS_REQUEST" });
        } else {
            // Warehouse changed mid-session — rebind activeFilter and
            // restart the lifecycle.
            dispatchAction({
                type: "SET_WAREHOUSE_ID",
                warehouseId: resolved,
            });
        }

        lastWarehouseIdRef.current = resolved;
    }, [resolution.warehouseId]);

    // ---- Counts effect -----------------------------------------------------
    //
    // Observe `status === "loading_counts"`. On success dispatch
    // COUNTS_SUCCESS followed by ITEMS_REQUEST to fetch page 1. On failure
    // dispatch COUNTS_FAILURE. The fetcher itself dispatches the error
    // modal via showError from api.ts.
    useEffect(() => {
        if (state.status !== "loading_counts") return;
        if (!state.activeFilter.warehouseId) return;

        let cancelled = false;

        (async () => {
            try {
                const counts = await fetchWorkItemCounts(
                    state.activeFilter.warehouseId,
                    state.activeFilter.startDate,
                );
                if (cancelled) return;
                dispatchAction({ type: "COUNTS_SUCCESS", counts });
                dispatchAction({ type: "ITEMS_REQUEST" });
            } catch (error) {
                if (cancelled) return;
                const message =
                    error instanceof Error
                        ? error.message
                        : "Unable to load work item counts.";
                dispatchAction({ type: "COUNTS_FAILURE", message });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        state.status,
        state.activeFilter.warehouseId,
        state.activeFilter.startDate,
    ]);

    // ---- Items effect ------------------------------------------------------
    //
    // Observe `status === "loading_items"`. Only fire a network call when
    // the current page has no cache entry and the buffer cannot yet satisfy
    // a full page — the buffer-first consumption rule. The filter captured
    // at request time is compared to the latest via the ref before we
    // dispatch ITEMS_SUCCESS; mismatches are silently discarded so stale
    // responses never land in the cache.
    useEffect(() => {
        if (state.status !== "loading_items") return;
        if (!state.activeFilter.warehouseId) return;
        if (state.pageCache[state.currentPage] !== undefined) return;
        if (state.forwardBuffer.length >= WORK_ITEMS_PAGE_SIZE) return;

        const filterAtRequestTime = state.activeFilter;
        const tokenAtRequestTime = state.nextToken ?? undefined;

        let cancelled = false;

        (async () => {
            try {
                const request: GetWorkItemsRequest = {
                    warehouseId: filterAtRequestTime.warehouseId,
                    processPaths: filterAtRequestTime.processPaths,
                    status: filterAtRequestTime.status,
                    startDate: filterAtRequestTime.startDate,
                    pageSize: WORK_ITEMS_PAGE_SIZE,
                    ...(tokenAtRequestTime
                        ? { nextToken: tokenAtRequestTime }
                        : {}),
                };

                const response = await fetchWorkItems(request);
                if (cancelled) return;

                // Provenance guard: discard the response if the user
                // changed the filter mid-flight.
                /* istanbul ignore if -- defense-in-depth behind the
                   `cancelled` flag set by the effect cleanup; React's
                   cleanup-before-rerun invariant means `cancelled` is
                   already true for any stale closure that could reach
                   here, so this branch is unreachable under normal
                   execution. */
                if (
                    !activeFilterMatches(
                        filterAtRequestTime,
                        activeFilterRef.current,
                    )
                ) {
                    return;
                }

                dispatchAction({
                    type: "ITEMS_SUCCESS",
                    items: response.workItems,
                    nextToken: response.nextToken ?? null,
                });
            } catch (error) {
                if (cancelled) return;
                const message =
                    error instanceof Error
                        ? error.message
                        : "Unable to load work items.";
                dispatchAction({ type: "ITEMS_FAILURE", message });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        state.status,
        state.currentPage,
        state.activeFilter,
        state.forwardBuffer.length,
        state.pageCache,
        state.nextToken,
    ]);

    // ---- Derived view-model ------------------------------------------------
    const nowMs = now().getTime();
    const rows = selectRenderedRows(state, nowMs);
    const canGoPrev = state.currentPage > 1;
    // Mirrors all four reachable transitions in the reducer's NAVIGATE_NEXT
    // (Case A: cached destination, Case B: full-page buffer drain,
    // Case B2: partial-buffer drain when MASS is exhausted, Case C: fetch
    // more via nextToken). Without the `forwardBuffer.length > 0` clause,
    // backend responses with `nextToken=null` and a between-1-and-PAGE_SIZE
    // overflow buffer left the Next button disabled even though the
    // reducer would happily render those trailing items as a final page.
    const canGoNext =
        state.pageCache[state.currentPage + 1] !== undefined ||
        state.forwardBuffer.length > 0 ||
        state.nextToken !== null;

    // ---- User action dispatchers ------------------------------------------
    const handleRefresh = () => dispatchAction({ type: "REFRESH" });
    const handleClearFilter = () => dispatchAction({ type: "CLEAR_FILTER" });
    const handleSelectProcessPath = (name: string) =>
        dispatchAction({ type: "SELECT_PROCESS_PATH", name });
    const handleNext = () => dispatchAction({ type: "NAVIGATE_NEXT" });
    const handlePrev = () => dispatchAction({ type: "NAVIGATE_PREV" });

    // ---- Render -----------------------------------------------------------
    return (
        <Box spacingInset="300" className="work-items-page">
            <Row
                alignmentVertical="center"
                alignmentHorizontal="justify"
                spacingInset="200 none"
            >
                <Heading type="h400" level={2}>
                    Work Items
                </Heading>
                <RefreshButton onClick={handleRefresh} />
            </Row>

            <CountsDashboard
                state={state}
                onClearFilter={handleClearFilter}
                onSelectProcessPath={handleSelectProcessPath}
            />

            <WorkItemsTable
                state={state}
                rows={rows}
                onToggleSort={(column) =>
                    dispatchAction({ type: "TOGGLE_SORT", column })
                }
            />

            <PaginationControls
                currentPage={state.currentPage}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPrev={handlePrev}
                onNext={handleNext}
            />
        </Box>
    );
};

/**
 * Structural equality for ActiveFilter values. Used by the items effect to
 * decide whether a response whose request was issued under a prior filter
 * should be silently discarded.
 */
const activeFilterMatches = (a: ActiveFilter, b: ActiveFilter): boolean =>
    a.warehouseId === b.warehouseId &&
    a.status === b.status &&
    a.startDate === b.startDate &&
    a.processPaths.length === b.processPaths.length &&
    a.processPaths.every((path, index) => path === b.processPaths[index]);

export default WorkItems;

import './index.scss';